import { useConnectorFlowRunner, type UseConnectorFlowRunnerResult } from '@/features/connectors/flows';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useEmailChannelFlow } from './useEmailChannelConnector';
import type { EmailFlowRole } from './constants';

type UseEmailConnectorFlowRunnerOptions = {
  audienceId: string | undefined;
  lang: string;
  /** Prefer this connector id when already known. */
  connectorId?: string;
};

export type UseEmailConnectorFlowRunnerResult = UseConnectorFlowRunnerResult & {
  workspaceId: string;
  flow: ReturnType<typeof useEmailChannelFlow>['flow'];
  connectorFlowId: string | undefined;
  connectorInstanceId: string | undefined;
  isChannelLoading: boolean;
};

/**
 * Email-specific composer: resolve the flow for a role, then run prepare/create.
 * Keeps {@link useConnectorFlowRunner} channel-agnostic.
 */
export const useEmailConnectorFlowRunner = (
  role: EmailFlowRole,
  { audienceId, lang, connectorId }: UseEmailConnectorFlowRunnerOptions
): UseEmailConnectorFlowRunnerResult => {
  const { id: workspaceId } = useWorkspace();
  const { flow, connectorInstanceId, isLoading: isChannelLoading } = useEmailChannelFlow(role, { connectorId });
  const connectorFlowId = flow?.id;

  const runner = useConnectorFlowRunner({
    workspaceId,
    audienceId,
    connectorFlowId,
    connectorInstanceId,
    lang,
  });

  return {
    ...runner,
    workspaceId,
    flow,
    connectorFlowId,
    connectorInstanceId,
    isChannelLoading,
  };
};
